import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Navbar from "@/components/Navbar";

const gsaSampleChallenges = [
  {
    challenge: "How can we reduce the average design-to-occupancy timeline for federal office renovations by 20%?",
    supportingKBs: ["Fast Track Scheduling Templates", "Modular Phasing Logic", "Common Bottlenecks in GSA Buildouts"]
  },
  {
    challenge: "Generate optimal layouts for hybrid workforce utilization in underused federal buildings.",
    supportingKBs: ["Hybrid Office Models", "Occupancy Patterns", "GSA Adjacency Rules"]
  },
  {
    challenge: "Recommend reconfigurable furniture solutions that reduce lifecycle costs by 30% across regional offices.",
    supportingKBs: ["Furniture Reuse Protocols", "Ergonomics Standards", "Procurement History"]
  },
  {
    challenge: "Identify early warning indicators for construction delays and scope changes in PBS capital projects.",
    supportingKBs: ["Lessons from Federal PMs", "Change Order Causality", "Cost-Time Risk Templates"]
  },
  {
    challenge: "Provide a code-compliant space plan for courthouse renovations under seismic and historic constraints.",
    supportingKBs: ["NEHRP + SHPO Rules", "Courthouse Program Models", "Space Allocation Templates"]
  },
  {
    challenge: "What are the best practices for incorporating daylighting and views into high-security facilities?",
    supportingKBs: ["Daylight-Wellness Tradeoffs", "ISC Design Overlays", "Secure Visibility Standards"]
  },
  {
    challenge: "Build a cross-agency LEED knowledge library to support greener renovation projects.",
    supportingKBs: ["LEED v4.1 Guidance", "Energy Modeling", "Material Specs Casebook"]
  },
  {
    challenge: "Create a planning tool for flexible floorplates adaptable to mission shifts and agency reorganizations.",
    supportingKBs: ["Space for Uncertainty", "Stack Planning Models", "Modular Program Zones"]
  },
  {
    challenge: "Establish a review framework for all new designs to auto-check ADA, egress, and fire codes.",
    supportingKBs: ["Annotated Code Checklists", "P100 Compliance Logic", "Reviewer Best Practices"]
  },
  {
    challenge: "What lessons from DOGE transitions can we embed into AI agents to reduce onboarding time for new PBS staff?",
    supportingKBs: ["DOGE Exit Intel", "Critical Role Mapping", "Institutional Memory KBs"]
  }
];

const GsaCallForSolutions = () => {
  const navigate = useNavigate();
  const [selectedChallenge, setSelectedChallenge] = useState<number | null>(null);

  const handleChallengeSelect = (index: number) => {
    if (index === 0) {
      // Challenge #1 goes to full CfS process
      navigate("/gsa-challenge-1/functional-area");
    } else {
      setSelectedChallenge(index);
    }
  };

  const handleProceed = () => {
    if (selectedChallenge !== null) {
      navigate("/gsa-call-for-solutions/bounty", {
        state: {
          selectedChallenge: gsaSampleChallenges[selectedChallenge]
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">GSA Call for Solutions Demo</h1>
          <p className="text-lg text-gray-600 mb-8">
            Select a representative GSA challenge to explore how iSPAI can help solve federal building and acquisition problems
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {gsaSampleChallenges.map((item, index) => (
            <Card 
              key={index}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                selectedChallenge === index 
                  ? 'border-2 border-blue-500 bg-blue-50' 
                  : 'border border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => handleChallengeSelect(index)}
            >
              <CardHeader>
                <CardTitle className="text-lg">
                  GSA Challenge #{index + 1}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`mb-4 font-medium ${
                  index === 0 
                    ? 'text-blue-600 underline cursor-pointer hover:text-blue-800' 
                    : 'text-gray-700'
                }`}>
                  {item.challenge}
                </p>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Supporting Human-Centric KBs:</p>
                  <div className="flex flex-wrap gap-2">
                    {item.supportingKBs.map((kb, kbIndex) => (
                      <span 
                        key={kbIndex}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {kb}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-center space-x-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-channel")}
            className="px-8 py-2"
          >
            Back to GSA Channel
          </Button>
          
          <Button 
            onClick={handleProceed}
            disabled={selectedChallenge === null}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            Create Call for Solutions
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaCallForSolutions;